import { FC, useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Chip,
    LinearProgress,
    Avatar,
    Card,
    CardContent,
    Link as MuiLink,
    Breadcrumbs,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Alert
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Verified as VerifiedIcon,
    Lock as LockIcon,
    Security as SecurityIcon,
    Apps as AppsIcon,
    Add as AddIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import { adminApiClient } from '../utils/adminApiClient';
import { useSnackbar } from 'notistack';

interface User {
    tid: string;
    profile: {
        firstname: string;
        lastname: string;
        email?: string;
        profileImage?: {
            data: string;
            contentType: string;
            uploadedAt: Date;
        };
    };
    account: {
        locked: boolean;
        verified: boolean;
        roles: Array<{
            id: string;
            name: string;
            ident: string;
        }>;
    };
}

interface AppUser {
    id?: string;
    _id?: string;
    tid: string;
    clientId: string | { toString(): string };
    clientApp?: {
        name: string;
    };
    scopes: string[];
    roles?: Array<{
        id: string;
        name: string;
        ident: string;
    }>;
    meta: Record<string, string>;
}

function getAppUserId(au: AppUser): string {
    return au._id ?? au.id ?? '';
}

export const UserDetailPage: FC = () => {
    const { tid } = useParams<{ tid: string }>();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [user, setUser] = useState<User | null>(null);
    const [appUsers, setAppUsers] = useState<AppUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingAppUsers, setLoadingAppUsers] = useState(true);
    const [systemRoles, setSystemRoles] = useState<Array<{ id: string; name: string; ident: string }>>([]);
    const [loadingSystemRoles, setLoadingSystemRoles] = useState(false);
    const [assignRoleDialogOpen, setAssignRoleDialogOpen] = useState(false);
    const [removeRoleDialogOpen, setRemoveRoleDialogOpen] = useState(false);
    const [deleteAppUserDialogOpen, setDeleteAppUserDialogOpen] = useState(false);
    const [appUserToDelete, setAppUserToDelete] = useState<AppUser | null>(null);

    useEffect(() => {
        if (tid) {
            loadUser();
            loadAppUsers();
        }
    }, [tid]);

    const loadUser = async () => {
        if (!tid) return;
        try {
            setLoading(true);
            const userData = await adminApiClient.getUserDetails(tid);
            setUser(userData);
        } catch (error: any) {
            console.error('Failed to load user:', error);
            enqueueSnackbar('Fehler beim Laden des Benutzers', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const loadAppUsers = async () => {
        if (!tid) return;
        try {
            setLoadingAppUsers(true);
            const appUsersData = await adminApiClient.getUserAppUsers(tid);
            // Convert clientId to string if it's an ObjectId
            const convertedAppUsers = appUsersData.map(au => ({
                ...au,
                clientId: String(au.clientId)
            }));
            setAppUsers(convertedAppUsers);
        } catch (error: any) {
            console.error('Failed to load app users:', error);
            enqueueSnackbar('Fehler beim Laden der App-Benutzer', { variant: 'error' });
        } finally {
            setLoadingAppUsers(false);
        }
    };

    const loadSystemRoles = async () => {
        try {
            setLoadingSystemRoles(true);
            const roles = await adminApiClient.getRoles();
            setSystemRoles(roles);
        } catch (error) {
            console.error('Failed to load system roles:', error);
            enqueueSnackbar('Fehler beim Laden der System-Rollen', { variant: 'error' });
        } finally {
            setLoadingSystemRoles(false);
        }
    };

    const handleAssignRole = async (roleIdent: string) => {
        if (!tid || !user) return;
        
        try {
            await adminApiClient.addUserRole(tid, roleIdent);
            enqueueSnackbar('System-Rolle erfolgreich zugewiesen', { variant: 'success' });
            setAssignRoleDialogOpen(false);
            loadUser();
        } catch (error) {
            console.error('Failed to assign role:', error);
            enqueueSnackbar('Fehler beim Zuweisen der System-Rolle', { variant: 'error' });
        }
    };

    const handleRemoveRole = async (roleIdent: string) => {
        if (!tid || !user) return;
        
        try {
            await adminApiClient.removeUserRole(tid, roleIdent);
            enqueueSnackbar('System-Rolle erfolgreich entfernt', { variant: 'success' });
            setRemoveRoleDialogOpen(false);
            loadUser();
        } catch (error) {
            console.error('Failed to remove role:', error);
            enqueueSnackbar('Fehler beim Entfernen der System-Rolle', { variant: 'error' });
        }
    };

    const getAvailableRoles = () => {
        if (!user) return [];
        const assignedRoleIdents = user.account.roles.map(role => role.ident);
        return systemRoles.filter(role => !assignedRoleIdents.includes(role.ident));
    };

    const getAssignedSystemRoles = () => {
        if (!user) return [];
        return user.account.roles.filter(role => 
            systemRoles.some(sr => sr.ident === role.ident)
        );
    };

    const handleOpenAssignRoleDialog = () => {
        loadSystemRoles();
        setAssignRoleDialogOpen(true);
    };

    const handleOpenRemoveRoleDialog = () => {
        loadSystemRoles();
        setRemoveRoleDialogOpen(true);
    };

    if (loading) {
        return (
            <Box sx={{ width: '100%' }}>
                <LinearProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box>
                <Typography variant="h6" color="error">
                    Benutzer nicht gefunden
                </Typography>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/app/admin/users')}
                    sx={{ mt: 2 }}
                >
                    Zurück zur Benutzerliste
                </Button>
            </Box>
        );
    }

    const getStatusChips = () => {
        const chips = [];
        
        if (user.account.verified) {
            chips.push(
                <Chip
                    key="verified"
                    icon={<VerifiedIcon />}
                    label="Verifiziert"
                    color="success"
                    size="small"
                    sx={{ mr: 1 }}
                />
            );
        } else {
            chips.push(
                <Chip
                    key="unverified"
                    label="Nicht verifiziert"
                    color="warning"
                    size="small"
                    sx={{ mr: 1 }}
                />
            );
        }
        
        if (user.account.locked) {
            chips.push(
                <Chip
                    key="locked"
                    icon={<LockIcon />}
                    label="Gesperrt"
                    color="error"
                    size="small"
                    sx={{ mr: 1 }}
                />
            );
        }
        
        const isAdmin = user.account.roles.some(role => role.ident === 'Admin' || role.ident === 'tnt:ids_admin');
        if (isAdmin) {
            chips.push(
                <Chip
                    key="admin"
                    icon={<SecurityIcon />}
                    label="Admin"
                    color="primary"
                    size="small"
                />
            );
        }
        
        return chips;
    };

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Breadcrumbs sx={{ mb: 2 }}>
                    <MuiLink
                        component={Link}
                        to="/app/admin/users"
                        sx={{ textDecoration: 'none' }}
                    >
                        Benutzer
                    </MuiLink>
                    <Typography color="text.primary">
                        {user.profile.firstname} {user.profile.lastname}
                    </Typography>
                </Breadcrumbs>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h4" component="h1" gutterBottom>
                            Benutzerdetails
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Profil, Account-Informationen und App-Benutzer
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ 
                display: 'grid', 
                gap: 3, 
                gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' }
            }}>
                {/* User Profile & Account Info */}
                <Box>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                                {user.profile.profileImage?.data ? (
                                    <Avatar
                                        src={`data:${user.profile.profileImage.contentType};base64,${user.profile.profileImage.data}`}
                                        sx={{ width: 100, height: 100, mb: 2 }}
                                    />
                                ) : (
                                    <Avatar sx={{ width: 100, height: 100, mb: 2 }}>
                                        {user.profile.firstname[0]}{user.profile.lastname[0]}
                                    </Avatar>
                                )}
                                <Typography variant="h5" component="h2" gutterBottom>
                                    {user.profile.firstname} {user.profile.lastname}
                                </Typography>
                                {user.profile.email && (
                                    <Typography variant="body2" color="text.secondary">
                                        {user.profile.email}
                                    </Typography>
                                )}
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    TID: {user.tid}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>

                    <Card sx={{ mt: 2 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Account-Status
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                                {getStatusChips()}
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle2">
                                    System-Rollen
                                </Typography>
                                <Box>
                                    <IconButton
                                        size="small"
                                        onClick={handleOpenAssignRoleDialog}
                                        color="primary"
                                        title="Rolle hinzufügen"
                                    >
                                        <AddIcon />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={handleOpenRemoveRoleDialog}
                                        color="error"
                                        title="Rolle entfernen"
                                        disabled={user.account.roles.length === 0}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {user.account.roles.length > 0 ? (
                                    user.account.roles.map((role) => (
                                        <Chip
                                            key={role.id}
                                            label={role.name}
                                            size="small"
                                            variant="outlined"
                                        />
                                    ))
                                ) : (
                                    <Typography variant="caption" color="text.secondary">
                                        Keine Rollen zugewiesen
                                    </Typography>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Box>

                {/* AppUsers List */}
                <Box>
                    <Paper variant="subtle">
                        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                            <Typography variant="h6" component="h2">
                                App-Benutzer
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Alle App-Benutzer dieses Benutzers
                            </Typography>
                        </Box>
                        {loadingAppUsers ? (
                            <Box sx={{ p: 2 }}>
                                <LinearProgress />
                            </Box>
                        ) : (
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Client</TableCell>
                                            <TableCell>Rollen</TableCell>
                                            <TableCell>Scopes</TableCell>
                                            <TableCell>Metadaten</TableCell>
                                            <TableCell align="right">Aktionen</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {appUsers.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center">
                                                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                                        Keine App-Benutzer gefunden
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            appUsers.map((appUser) => (
                                                <TableRow key={getAppUserId(appUser)} hover>
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {appUser.clientApp?.name || 'Unbekannt'}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {typeof appUser.clientId === 'string' ? appUser.clientId : appUser.clientId.toString()}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                            {appUser.roles && appUser.roles.length > 0 ? (
                                                                appUser.roles.map((role) => (
                                                                    <Chip
                                                                        key={role.id}
                                                                        label={role.name || role.ident}
                                                                        size="small"
                                                                        variant="outlined"
                                                                    />
                                                                ))
                                                            ) : (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Keine Rollen
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                            {appUser.scopes && appUser.scopes.length > 0 ? (
                                                                appUser.scopes.map((scope, index) => (
                                                                    <Chip
                                                                        key={index}
                                                                        label={scope}
                                                                        size="small"
                                                                        color="secondary"
                                                                        variant="outlined"
                                                                    />
                                                                ))
                                                            ) : (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Keine Scopes
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="caption">
                                                            {appUser.meta && Object.keys(appUser.meta).length > 0
                                                                ? `${Object.keys(appUser.meta).length} Einträge`
                                                                : 'Keine Metadaten'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            component={Link}
                                                            to={`/app/admin/users/${tid}/app-users/${getAppUserId(appUser)}`}
                                                            startIcon={<AppsIcon />}
                                                        >
                                                            Bearbeiten
                                                        </Button>
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            title="App-Benutzer löschen"
                                                            onClick={() => {
                                                                setAppUserToDelete(appUser);
                                                                setDeleteAppUserDialogOpen(true);
                                                            }}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Paper>
                </Box>
            </Box>

            {/* Delete AppUser Confirmation Dialog */}
            <Dialog
                open={deleteAppUserDialogOpen}
                onClose={() => {
                    setDeleteAppUserDialogOpen(false);
                    setAppUserToDelete(null);
                }}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>App-Benutzer löschen</DialogTitle>
                <DialogContent>
                    <Typography>
                        {appUserToDelete
                            ? `App-Benutzer für „${appUserToDelete.clientApp?.name || 'Unbekannt'}“ wirklich löschen?`
                            : ''}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setDeleteAppUserDialogOpen(false);
                            setAppUserToDelete(null);
                        }}
                    >
                        Abbrechen
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={async () => {
                            if (!tid || !appUserToDelete) return;
                            try {
                                const appUserIdToDelete = getAppUserId(appUserToDelete);
                                if (!appUserIdToDelete) {
                                    enqueueSnackbar('App-Benutzer-ID fehlt', { variant: 'error' });
                                    return;
                                }
                                await adminApiClient.deleteAppUser(tid, appUserIdToDelete);
                                setDeleteAppUserDialogOpen(false);
                                setAppUserToDelete(null);
                                await loadAppUsers();
                                enqueueSnackbar('App-Benutzer erfolgreich gelöscht', { variant: 'success' });
                            } catch (error: unknown) {
                                console.error('Failed to delete app user:', error);
                                enqueueSnackbar('Fehler beim Löschen des App-Benutzers', { variant: 'error' });
                            }
                        }}
                    >
                        Löschen
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Assign System Role Dialog */}
            <Dialog 
                open={assignRoleDialogOpen} 
                onClose={() => setAssignRoleDialogOpen(false)} 
                maxWidth="sm" 
                fullWidth
            >
                <DialogTitle>
                    System-Rolle zuweisen für {user?.profile.firstname} {user?.profile.lastname}
                </DialogTitle>
                <DialogContent>
                    {loadingSystemRoles ? (
                        <Box sx={{ p: 2 }}>
                            <LinearProgress />
                        </Box>
                    ) : (
                        <Box sx={{ pt: 2 }}>
                            {getAvailableRoles().length === 0 ? (
                                <Alert severity="info">
                                    Alle verfügbaren System-Rollen sind bereits zugewiesen.
                                </Alert>
                            ) : (
                                <List>
                                    {getAvailableRoles().map((role) => (
                                        <ListItem key={role.id} disablePadding>
                                            <ListItemButton onClick={() => handleAssignRole(role.ident)}>
                                                <ListItemText
                                                    primary={role.name}
                                                    secondary={role.ident}
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAssignRoleDialogOpen(false)}>Abbrechen</Button>
                </DialogActions>
            </Dialog>

            {/* Remove System Role Dialog */}
            <Dialog 
                open={removeRoleDialogOpen} 
                onClose={() => setRemoveRoleDialogOpen(false)} 
                maxWidth="sm" 
                fullWidth
            >
                <DialogTitle>
                    System-Rolle entfernen von {user?.profile.firstname} {user?.profile.lastname}
                </DialogTitle>
                <DialogContent>
                    {loadingSystemRoles ? (
                        <Box sx={{ p: 2 }}>
                            <LinearProgress />
                        </Box>
                    ) : (
                        <Box sx={{ pt: 2 }}>
                            {getAssignedSystemRoles().length === 0 ? (
                                <Alert severity="info">
                                    Keine System-Rollen zugewiesen.
                                </Alert>
                            ) : (
                                <List>
                                    {getAssignedSystemRoles().map((role) => (
                                        <ListItem key={role.id} disablePadding>
                                            <ListItemButton onClick={() => handleRemoveRole(role.ident)}>
                                                <ListItemText
                                                    primary={role.name}
                                                    secondary={role.ident}
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRemoveRoleDialogOpen(false)}>Abbrechen</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

